import { useState } from 'react';
import { AnswerOptionCondition, AnswerOptionConditionType } from 'shared/types/due-diligence';
import { NaturalLanguageSentence } from './NaturalLanguageSentence';
import { Button } from '@/components/ui/button';
import { Trash } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';

// Import editors
import { FileExistsEditor } from './editors/FileExistsEditor';
import { ExpirationDateEditor } from './editors/ExpirationDateEditor';
import { FieldValueEditor } from './editors/FieldValueEditor';
import { AIQuestionEditor } from './editors/AIQuestionEditor';
import { AIReferenceEditor } from './editors/AIReferenceEditor';
import { DatabaseComparisonEditor } from './editors/DatabaseComparisonEditor';
import { QuestionAnswerEditor } from './editors/QuestionAnswerEditor';

interface ConditionNodeRendererProps {
  condition: AnswerOptionCondition;
  linkedLabels: Array<{ id: number; name: string }>;
  hasMultipleLabels: boolean;
  extractionQuestions: Array<{
    fieldName: string;
    displayName: string;
    extractionLabel?: string | null;
    extractionValueType?: 'number' | 'string' | 'date' | 'boolean' | null;
  }>;
  availableQuestions: Array<{
    id: number;
    displayName: string;
    fieldName: string;
    answerOptions?: Array<{
      id?: number;
      label: string;
      value: string;
      statusColor: string;
      active: boolean;
    }>;
  }>;
  currentQuestionId?: number;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
  onRemove: () => void;
}

export const ConditionNodeRenderer: React.FC<ConditionNodeRendererProps> = ({
  condition,
  linkedLabels,
  hasMultipleLabels,
  extractionQuestions,
  availableQuestions,
  currentQuestionId,
  onUpdate,
  onRemove,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleLabelChange = (labelId: number) => {
    onUpdate({ labelId });
  };

  const renderEditor = () => {
    switch (condition.type) {
      case AnswerOptionConditionType.FILE_EXISTS:
        return <FileExistsEditor condition={condition} onUpdate={onUpdate} />;
      case AnswerOptionConditionType.EXPIRATION_DATE:
        return <ExpirationDateEditor condition={condition} onUpdate={onUpdate} />;
      case AnswerOptionConditionType.FIELD_VALUE:
        return <FieldValueEditor condition={condition} onUpdate={onUpdate} />;
      case AnswerOptionConditionType.AI_QUESTION:
        return <AIQuestionEditor condition={condition} onUpdate={onUpdate} />;
      case AnswerOptionConditionType.AI_REFERENCE:
        return <AIReferenceEditor condition={condition} onUpdate={onUpdate} />;
      case AnswerOptionConditionType.DATABASE_COMPARISON:
        return (
          <DatabaseComparisonEditor
            condition={condition}
            onUpdate={onUpdate}
            extractionQuestions={extractionQuestions}
          />
        );
      case AnswerOptionConditionType.QUESTION_ANSWER:
        return (
          <QuestionAnswerEditor
            condition={condition}
            onUpdate={onUpdate}
            availableQuestions={availableQuestions}
            currentQuestionId={currentQuestionId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-2">
      {/* Label selector for multi-label scenarios */}
      {hasMultipleLabels && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Van toepassing op label:</span>
          <SimpleDropdown
            value={condition.labelId?.toString() || ''}
            onChange={(value: string) => handleLabelChange(parseInt(value))}
            options={linkedLabels.map((label) => ({
              value: label.id.toString(),
              label: label.name,
            }))}
            placeholder="Selecteer label"
            className="min-w-[200px]"
          />
        </div>
      )}

      {/* Natural language sentence + delete button */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <NaturalLanguageSentence
            condition={condition}
            isEditing={isEditing}
            onClick={handleToggleEdit}
          />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className={cn(
            'flex-shrink-0 h-9 w-9 p-0',
            'text-gray-400 hover:text-red-600 hover:bg-red-50'
          )}
        >
          <Trash size={16} weight="regular" />
        </Button>
      </div>

      {/* Editor panel (when expanded) */}
      {isEditing && (
        <div className="pl-8 pr-2 py-3 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-2 duration-200">
          {renderEditor()}
        </div>
      )}
    </div>
  );
};
